import "./App.css";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import AppRoute from "./appRoute/AppRoute";
import ErrorBoundary from "./components/ErrorBoundary";

import { ConfigProvider, theme } from "antd";
import enUS from "antd/locale/en_US";
import hyAM from "antd/locale/hy_AM";
import ruRU from "antd/locale/ru_RU";
import "dayjs/locale/hy-am";
import "dayjs/locale/ru";
import { useTranslation } from "react-i18next";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { useEffect, useState } from "react";
import { applyTheme, getSavedTheme, THEME_CHANGED } from "./utils/theme";
import {
  changeLanguage,
  getSavedLanguage,
  normalizeLanguage,
} from "./utils/language";

const UI_LOCALES = { am: hyAM, en: enUS, ru: ruRU };

// WEB-07: removed React Query — it was provider-wrapped app-wide but used in exactly one
// place (a single Google-login mutation, now a plain async handler). Every other server
// call already goes through the Redux thunk/try-catch pattern.

// WEB-11: CRA only inlines REACT_APP_* env vars, so REACT_GOOGLE_CLIENT_ID was
// always undefined and silently fell back to the hardcoded ID.
const GOOGLE_CLIENT_ID =
  process.env.REACT_APP_GOOGLE_CLIENT_ID ||
  "974447978159-b0l9mbj8tqisrn58ogga4t53g1m0qav5.apps.googleusercontent.com";

function App() {
  const { t, i18n } = useTranslation();
  const language = normalizeLanguage(i18n.resolvedLanguage || i18n.language);
  // Default to light unless the user has explicitly saved a dark theme.
  const [isDark, setIsDark] = useState(() => getSavedTheme() === "dark");

  useEffect(() => {
    const syncSavedTheme = (event) => {
      if (
        event.type === "storage" &&
        event.key !== "theme" &&
        event.key !== null
      )
        return;
      setIsDark((event.detail || getSavedTheme()) === "dark");
    };
    window.addEventListener(THEME_CHANGED, syncSavedTheme);
    window.addEventListener("storage", syncSavedTheme);
    return () => {
      window.removeEventListener(THEME_CHANGED, syncSavedTheme);
      window.removeEventListener("storage", syncSavedTheme);
    };
  }, []);

  useEffect(() => {
    applyTheme(isDark ? "dark" : "light");
  }, [isDark]);

  useEffect(() => {
    const syncLanguage = (event) => {
      if (event.key === "language" || event.key === null) {
        changeLanguage(getSavedLanguage());
      }
    };
    window.addEventListener("storage", syncLanguage);
    return () => window.removeEventListener("storage", syncLanguage);
  }, []);

  return (
    <ConfigProvider
      locale={UI_LOCALES[language]}
      theme={{
        algorithm: isDark ? theme.darkAlgorithm : theme.defaultAlgorithm,
        token: {
          fontFamily: '"Montserratarm", "Segoe UI", Arial, sans-serif',
        },
      }}
    >
      <div className="App">
        <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
          <ErrorBoundary>
            <AppRoute />
          </ErrorBoundary>
          <ToastContainer
            aria-label={t("common.notificationsRegion")}
            style={{ width: "auto" }}
            theme={isDark ? "dark" : "light"}
          />
        </GoogleOAuthProvider>
      </div>
    </ConfigProvider>
  );
}

export default App;
